// components/ReportedDocumentsTable/ReportedDocumentsTable.tsx
import React, { useState, useEffect, useContext } from "react";
import { getReportedDocuments, ReportedDocument, deleteReportedDocument } from "features/signaler-document";
import './ReportedDocumentsTable.scss';
import ToggleIdNumber from "components/ToggleIdNumber";
import { UserContext } from "features/account";

interface ReportedDocumentsTableProps {
  companyName?: string;
  refreshTrigger?: boolean;
  onDelete?: () => void; // Optional callback to refresh parent component
}

const ReportedDocumentsTable: React.FC<ReportedDocumentsTableProps> = ({
                                                                         companyName = '',
                                                                         refreshTrigger,
                                                                         onDelete
                                                                       }) => {
  const [documents, setDocuments] = useState<ReportedDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const { user, userType } = useContext(UserContext);

  // Check if user is admin based on partnerRole
  const isAdmin = user?.partnerRole === 'admin';
  const userRole = userType || 'member';

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getReportedDocuments(companyName);
      if (response.success) {
        setDocuments(response.data || []);
      } else {
        setDocuments([]);
      }
    } catch (err: any) {
      console.error('Error fetching documents:', err);
      setError(err.response?.data?.error || 'Erreur lors du chargement');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyName) {
      fetchDocuments();
    }
  }, [companyName, refreshTrigger]);

  const handleDelete = async (id: number) => {
    if (!isAdmin) {
      setDeleteError("Vous n'avez pas les droits pour supprimer ce signalement");
      setTimeout(() => setDeleteError(null), 3000);
      return;
    }

    if (window.confirm("Êtes-vous sûr de vouloir supprimer ce signalement ? Cette action est irréversible.")) {
      setDeletingId(id);
      setDeleteError(null);
      try {
        const response = await deleteReportedDocument(id, userRole);
        if (response.success) {
          // Remove the deleted document from the list
          setDocuments(prev => prev.filter(doc => doc.id !== id));
          // Notify parent component to refresh if callback provided
          if (onDelete) {
            onDelete();
          }
        } else {
          setDeleteError(response.error || 'Erreur lors de la suppression');
          setTimeout(() => setDeleteError(null), 3000);
        }
      } catch (err: any) {
        console.error('Error deleting document:', err);
        setDeleteError(err.response?.data?.error || 'Erreur lors de la suppression');
        setTimeout(() => setDeleteError(null), 3000);
      } finally {
        setDeletingId(null);
      }
    }
  };

  const getMotifBadgeClass = (motif: string) => {
    return motif === 'Fraude avérée' ? 'badge-fraude' : 'badge-usurpation';
  };

  const getDocumentTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      'passport': 'Passeport',
      'id_card': "Carte d'identité",
      'residency_permit': 'Carte de séjour',
      'driver_permit': 'Permis de conduire'
    };
    return types[type] || type;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="reported-documents-table">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Chargement des signalements...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reported-documents-table">
        <div className="error-state">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reported-documents-table">
      <div className="table-header">
        <h3>Nos signalements <span className="text-xs text-muted-foreground">({user?.companyName ?? ''})</span></h3>
        <div className="table-header-right">
          <span className="badge-count">{documents.length} document(s)</span>
          {!isAdmin && (
            <span className="role-badge role-member">
              Mode consultation
            </span>
          )}
          {isAdmin && (
            <span className="role-badge role-admin">
              Mode administration
            </span>
          )}
        </div>
      </div>

      {deleteError && (
        <div className="delete-error-message">
          {deleteError}
        </div>
      )}

      {documents.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📄</div>
          <p>Aucun signalement pour le moment</p>
          <small>Les documents que vous signalez apparaîtront ici</small>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table">
            <thead>
            <tr>
              <th>Pays émetteur</th>
              <th>Type de document</th>
              <th>Numéro de document</th>
              <th>Motif</th>
              <th>Date de signalement</th>
              <th className="actions-column">Actions</th>
            </tr>
            </thead>
            <tbody>
            {documents.map((doc) => (
              <tr key={doc.id} className={deletingId === doc.id ? 'deleting-row' : ''}>
                <td>{doc.paysEmetteur}</td>
                <td>{getDocumentTypeLabel(doc.typeDocument)}</td>
                <td className="document-number">
                  <ToggleIdNumber number={doc.numeroDocument} />
                </td>
                <td>
                    <span className={`motif-badge ${getMotifBadgeClass(doc.motif)}`}>
                      {doc.motif}
                    </span>
                </td>
                <td>{formatDate(doc.createdAt)}</td>
                <td className="actions-column">
                  <button
                    className={`delete-btn ${!isAdmin ? 'disabled' : ''}`}
                    onClick={() => handleDelete(doc.id)}
                    disabled={!isAdmin || deletingId === doc.id}
                    title={!isAdmin ? "Seul un administrateur peut supprimer un signalement" : "Supprimer ce signalement"}
                  >
                    {deletingId === doc.id ? (
                      <span className="spinner-small"></span>
                    ) : (
                      <span>🗑️</span>
                    )}
                    <span className="btn-text">Supprimer</span>
                  </button>
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ReportedDocumentsTable;